// Corporate document requirements per country (Firestore corporateDocumentRequirements/{ISO2}).
//
// Record (format 2):
//   { iso2, country, cases: [{ name, logic: "ALL" | "ONE_OF",
//       docs: [{ local, english, type, optional, contains: {...}, equivalent: {...}, details_note }] }],
//     guidance, issuer, notes, legal_system, available_online, free_access, online_notes, official_url,
//     format: 2, updatedAt, updatedByEmail }
// A country lists every case (e.g. UAE mainland / free zone); the requirement is complete when any
// one case is complete, and staff review the documents manually. An optional document is listed
// but does not count for completion.
// The client document upload API (myKyc_client_doc_upload.js) builds the same item keys.

export const CORPORATE_DOCUMENT_REQUIREMENT_COLLECTION = "corporateDocumentRequirements";
export const CORPORATE_DOCUMENT_FALLBACK_LABEL =
  "Company register extract or equivalent corporate registration document";

export const CORPORATE_DOC_TYPES = [
  { value: "registration", label: "Registration" },
  { value: "constitutional", label: "Constitutional" },
  { value: "ownership", label: "Ownership" },
  { value: "good_standing", label: "Good standing" },
];

// What a document shows, and which common document it is equivalent to: "yes" | "partly" | "no" | "unknown".
export const CORPORATE_DOC_CONTENT_FIELDS = [
  { key: "name", label: "Name", short: "Name" },
  { key: "legal_form", label: "Legal form", short: "Form" },
  { key: "address", label: "Address", short: "Address" },
  { key: "management", label: "Senior management (names)", short: "Mgmt" },
  { key: "shareholders", label: "Shareholders", short: "Shareh." },
];
export const CORPORATE_DOC_EQUIVALENT_FIELDS = [
  { key: "articles", label: "Articles of association", short: "AoA" },
  { key: "register_extract", label: "Extract of the register (civil law)", short: "Extract" },
  { key: "incorporation_certificate", label: "Certificate of incorporation (common law)", short: "CoI" },
  { key: "incumbency_certificate", label: "Certificate of incumbency (common law)", short: "Incumb." },
  { key: "moa", label: "Memorandum of association (MoA)", short: "MoA" },
];
export const CORPORATE_DETAIL_VALUES = [
  { value: "yes", label: "Yes", symbol: "✓" },
  { value: "partly", label: "Partly", symbol: "◐" },
  { value: "no", label: "No", symbol: "✗" },
  { value: "unknown", label: "Unknown", symbol: "?" },
];
const DETAIL_VALUE_SET = new Set(CORPORATE_DETAIL_VALUES.map((v) => v.value));

// { key: value } for the given fields, "unknown" when missing or invalid.
export function corporateDocDetails(source, fields) {
  return Object.fromEntries(fields.map(({ key }) => {
    const value = clean(source?.[key]).toLowerCase();
    return [key, DETAIL_VALUE_SET.has(value) ? value : "unknown"];
  }));
}

export const CORPORATE_CASE_LOGICS = [
  { value: "ALL", label: "All required" },
  { value: "ONE_OF", label: "One of" },
];

const clean = (value) => String(value || "").trim();

function slugKey(value, fallback = "item") {
  const slug = clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 80);
  return slug || fallback;
}

export function corporateDocLabel(item) {
  return clean(item?.local) || clean(item?.english);
}

function corporateDocSecondaryLabel(item) {
  const local = clean(item?.local);
  const english = clean(item?.english);
  return local && english && english.toLowerCase() !== local.toLowerCase() ? english : "";
}

// Guess a document type from its names (editable afterwards).
export function guessCorporateDocType(item) {
  const text = `${item?.local || ""} ${item?.english || ""}`.toLowerCase();
  if (/good standing|compliance certificate|certificate of compliance/.test(text)) return "good_standing";
  if (/shareholder|incumbency|register of members|members register|company search|gesellschafterliste/.test(text)) return "ownership";
  if (/memorandum|articles|statut|constitution|operating agreement|contrato social|estatuto|bylaws|deed of/.test(text)) return "constitutional";
  return "registration";
}

function normalizeLogic(value) {
  return clean(value).toUpperCase() === "ONE_OF" ? "ONE_OF" : "ALL";
}

// Cases of a stored record.
export function corporateRequirementCases(data = {}) {
  return (Array.isArray(data?.cases) ? data.cases : [])
    .map((c) => ({
      name: clean(c?.name),
      logic: normalizeLogic(c?.logic),
      docs: (Array.isArray(c?.docs) ? c.docs : [])
        .map((d) => ({
          local: clean(d?.local),
          english: clean(d?.english),
          type: clean(d?.type) || guessCorporateDocType(d),
          optional: d?.optional === true,
          contains: corporateDocDetails(d?.contains, CORPORATE_DOC_CONTENT_FIELDS),
          equivalent: corporateDocDetails(d?.equivalent, CORPORATE_DOC_EQUIVALENT_FIELDS),
          details_note: clean(d?.details_note),
        }))
        .filter((d) => d.local || d.english),
    }))
    .filter((c) => c.docs.length);
}

function fallbackRequirement(iso2) {
  const key = "company_register_extract";
  return {
    iso2,
    fallback: true,
    doc_logic: "ALL",
    doc_condition: null,
    docs: [{ key, label: CORPORATE_DOCUMENT_FALLBACK_LABEL, secondaryLabel: "" }],
    cases: [{ name: "", logic: "ALL", keys: [key], optionalKeys: [] }],
  };
}

// Requirement used by the KYC documents checklist: the documents of all cases (one item per distinct
// document, so a document shared by two cases is ticked once) and, per case, the keys of its
// required documents (keys) and of its optional ones (optionalKeys).
export function corporateRequirementFromData(iso2, data) {
  const cases = data ? corporateRequirementCases(data) : [];
  if (!cases.length) return fallbackRequirement(iso2);
  const docs = [];
  const keyByLabel = new Map();
  const keyOf = (d) => {
    const label = corporateDocLabel(d);
    const id = label.toLowerCase();
    if (!keyByLabel.has(id)) {
      const key = `${docs.length + 1}_${slugKey(label, `doc_${docs.length + 1}`)}`;
      keyByLabel.set(id, key);
      docs.push({ key, label, secondaryLabel: corporateDocSecondaryLabel(d), type: d.type });
    }
    return keyByLabel.get(id);
  };
  const outCases = cases.map((c) => {
    const keyed = c.docs.map((d) => ({ key: keyOf(d), optional: d.optional }));
    return {
      name: c.name,
      logic: c.logic,
      keys: keyed.filter((d) => !d.optional).map((d) => d.key),
      optionalKeys: keyed.filter((d) => d.optional).map((d) => d.key),
    };
  });
  return {
    iso2,
    fallback: false,
    doc_logic: outCases.length > 1 ? "CASES" : outCases[0].logic,
    doc_condition: clean(data?.guidance) || null,
    docs,
    cases: outCases,
  };
}

function caseSatisfied(regDoc, kase) {
  const obtained = kase.keys.filter((key) => !!regDoc?.items?.[key]?.obtained).length;
  return kase.logic === "ONE_OF" ? obtained > 0 : obtained === kase.keys.length;
}

// Cases of a requirement object; one built from docs + doc_logic reads as a single case.
function requirementCases(requirement) {
  if (Array.isArray(requirement?.cases)) return requirement.cases;
  const keys = (requirement?.docs || []).map((item) => item.key).filter(Boolean);
  if (!keys.length) return [];
  const logic = clean(requirement?.doc_logic).toUpperCase();
  return [{ name: "", logic: logic === "ONE_OF" || logic === "CONDITIONAL" ? "ONE_OF" : "ALL", keys }];
}

// Complete when any one case is complete.
export function corporateRequirementSatisfied(regDoc, requirement) {
  const cases = requirementCases(requirement);
  if (!cases.length) return !!regDoc?.obtained;
  return cases.some((kase) => caseSatisfied(regDoc, kase));
}

// Progress of the most advanced case.
export function corporateRequirementProgress(regDoc, requirement) {
  const cases = requirementCases(requirement);
  if (!cases.length) return { required: 1, obtained: regDoc?.obtained ? 1 : 0 };
  return cases
    .map((kase) => {
      const obtained = kase.keys.filter((key) => !!regDoc?.items?.[key]?.obtained).length;
      return kase.logic === "ONE_OF"
        ? { required: 1, obtained: obtained > 0 ? 1 : 0 }
        : { required: kase.keys.length, obtained };
    })
    .sort((a, b) => b.obtained / b.required - a.obtained / a.required)[0];
}

// Cases as stored on a checklist snapshot (kyc documentsChecklist ... docs.reg.cases).
export function corporateRequirementSnapshotCases(requirement) {
  return (requirement?.cases || []).map((kase) => ({
    name: kase.name,
    logic: kase.logic,
    keys: kase.keys,
    optionalKeys: kase.optionalKeys || [],
  }));
}

// ---------------------------------------------------------------------------------------------
// CSV export: one row per document.

const CSV_COLUMNS = [
  ["ISO 2", (r) => r.iso2],
  ["Country", (r) => r.country],
  ["Case", (r, c) => c.name],
  ["Rule", (r, c) => (c.logic === "ONE_OF" ? "One of" : "All required")],
  ["Document (local)", (r, c, d) => d.local],
  ["Document (English)", (r, c, d) => d.english],
  ["Type", (r, c, d) => CORPORATE_DOC_TYPES.find((t) => t.value === d.type)?.label || d.type],
  ["Optional", (r, c, d) => (d.optional ? "Yes" : "")],
  ...CORPORATE_DOC_CONTENT_FIELDS.map(({ key, label }) => [`Contains: ${label}`, (r, c, d) => d.contains?.[key] || "unknown"]),
  ...CORPORATE_DOC_EQUIVALENT_FIELDS.map(({ key, label }) => [`Equivalent: ${label}`, (r, c, d) => d.equivalent?.[key] || "unknown"]),
  ["Details note", (r, c, d) => d.details_note],
  ["Guidance", (r) => r.guidance],
  ["Issuer", (r) => r.issuer],
  ["Notes", (r) => r.notes],
  ["Legal system", (r) => r.legal_system],
  ["Available online", (r) => r.available_online],
  ["Free access", (r) => r.free_access],
  ["Online notes", (r) => r.online_notes],
  ["Official URL", (r) => r.official_url],
];

function csvCell(value) {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function corporateRequirementsCsv(records = []) {
  const lines = [CSV_COLUMNS.map(([header]) => csvCell(header)).join(",")];
  records.forEach((raw) => {
    corporateRequirementCases(raw).forEach((c) => {
      c.docs.forEach((d) => {
        lines.push(CSV_COLUMNS.map(([, get]) => csvCell(get(raw, c, d))).join(","));
      });
    });
  });
  return lines.join("\r\n");
}
